import { createFileRoute } from "@tanstack/react-router";
import { Award, Wifi, Clock, ChevronDown, Check } from "lucide-react";
import { useState } from "react";
import { ImersaoPbeQualifierForm } from "@/components/imersao-pbe/ImersaoPbeQualifierForm";
import logoPbe from "@/assets/imersao-pbe/logo-pbe.png.asset.json";
import heroBg from "@/assets/imersao-pbe/hero-molecular.webp.asset.json";
import speakersTie from "@/assets/imersao-pbe/speakers-tie-bruno.webp.asset.json";
import speakersLuciola from "@/assets/imersao-pbe/speakers-luciola-leo.webp.asset.json";

export const Route = createFileRoute("/imersao-pbe")({
  head: () => ({
    meta: [
      { title: "Imersão PBE" },
      {
        name: "description",
        content:
          "Imersão em Prática Baseada em Evidências: aprenda a buscar, interpretar e aplicar artigos científicos na sua prática clínica. Online, 100% gratuita e com certificado.",
      },
      { property: "og:title", content: "Imersão PBE" },
      {
        property: "og:description",
        content:
          "Aprenda a buscar, interpretar e aplicar artigos científicos para melhorar seus tratamentos. Online · 100% gratuito · Com certificado.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://fisioemortopedia.com.br/imersao-pbe" },
    ],
    links: [{ rel: "canonical", href: "https://fisioemortopedia.com.br/imersao-pbe" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Course",
          name: "Imersão em Prática Baseada em Evidências (PBE)",
          description:
            "Imersão online e gratuita que ensina fisioterapeutas a buscar, interpretar e aplicar artigos científicos na tomada de decisão clínica. Com certificado de 5h.",
          url: "https://fisioemortopedia.com.br/imersao-pbe",
          inLanguage: "pt-BR",
          educationalLevel: "Profissional / Pós-graduação",
          provider: {
            "@type": "Organization",
            name: "Fisio em Ortopedia",
            sameAs: "https://fisioemortopedia.com.br",
          },
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "BRL",
            category: "Free",
            availability: "https://schema.org/InStock",
          },
          hasCourseInstance: {
            "@type": "CourseInstance",
            courseMode: "online",
            courseWorkload: "PT5H",
            inLanguage: "pt-BR",
          },
        }),
      },
    ],
  }),
  component: ImersaoPbePage,
});

const FAQS = [
  {
    q: "A Inscrição é gratuita?",
    a: "Sim, a Imersão em Prática Baseada em Evidências é 100% online e gratuita. São aulas gravadas que serão disponibilizadas diretamente na nossa plataforma de ensino.",
  },
  {
    q: "Ainda não sou formado(a) em fisioterapia, posso participar?",
    a: "Pode sim! A Imersão é para estudantes de fisioterapia e fisioterapeutas.",
  },
  {
    q: "Como vou acessar a imersão?",
    a: "Após realizar sua inscrição, você receberá por e-mail todas as orientações para acessar a plataforma e acompanhar as aulas.",
  },
  {
    q: "Preciso ter conhecimento prévio em Prática Baseada em Evidências?",
    a: "Não. As aulas foram estruturadas para conduzir você desde os conceitos fundamentais até a aplicação prática da PBE na tomada de decisão clínica.",
  },
  {
    q: "Tem certificado?",
    a: "Sim, a Imersão em PBE tem certificado de 5h!",
  },
];

function ImersaoPbePage() {
  return (
    <main className="min-h-screen bg-[#0a1420] text-white antialiased">
      {/* HERO */}
      <section
        id="topo"
        className="relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(10,20,32,0.82), rgba(10,20,32,0.9)), url(${heroBg.url})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-[#3fd5c9]/20 blur-[140px]" />
          <div className="absolute top-10 right-[-140px] h-[420px] w-[420px] rounded-full bg-[#6c63ff]/25 blur-[130px]" />
        </div>

        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 pt-10 sm:pt-16 pb-20 sm:pb-28">
          <div className="grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            {/* left */}
            <div className="text-center sm:text-left">
              <img decoding="async"
                src={logoPbe.url}
                alt="Imersão em PBE"
                className="mx-auto sm:mx-0 h-40 sm:h-52 w-auto drop-shadow-2xl"
              />

              <p className="mt-8 max-w-xl text-xl sm:text-2xl text-white/85 leading-snug mx-auto sm:mx-0">
                Aprenda a <strong className="text-white">buscar</strong>,{" "}
                <strong className="text-white">interpretar</strong> e{" "}
                <strong className="text-white">aplicar artigos científicos</strong> para melhorar seus tratamentos.
              </p>


              <div className="mt-8 flex flex-wrap justify-center sm:justify-start gap-3 text-xs sm:text-sm">
                <Chip icon={<Wifi className="w-4 h-4" />}>Online</Chip>
                <Chip icon={<Clock className="w-4 h-4" />}>100% gratuito</Chip>
                <Chip icon={<Award className="w-4 h-4" />}>Com Certificado</Chip>
              </div>
            </div>

            {/* right — form */}
            <div className="relative">
              <div className="absolute -inset-4 rounded-[32px] bg-gradient-to-br from-[#6c63ff]/40 via-[#3fd5c9]/20 to-transparent blur-2xl" />
              <div className="relative rounded-3xl bg-white p-6 sm:p-8 shadow-2xl shadow-black/60 ring-1 ring-black/5 text-slate-900">
                <ImersaoPbeQualifierForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOBRE: FEO + PEDro */}
      <section className="relative bg-[#f4f7fb] text-slate-900 px-4 sm:px-6 py-20 sm:py-28">
        <div className="mx-auto max-w-4xl">
          <span className="text-xs font-bold uppercase tracking-[0.28em] text-[#6c63ff]">
            Fisio em Ortopedia + PEDro
          </span>
          <h2 className="mt-4 text-3xl sm:text-5xl font-black tracking-tight">
            <span className="bg-gradient-to-r from-[#6c63ff] to-[#3fd5c9] bg-clip-text text-transparent">
              Fisio em Ortopedia
            </span>{" "}
            + PEDro
          </h2>

          <div className="mt-8 space-y-5 text-base sm:text-lg text-slate-700 leading-relaxed">
            <p>
              Em parceria com a <strong>PEDro</strong> (Physiotherapy Evidence Database), uma das principais
              bases de dados em fisioterapia baseada em evidências, que reúne ensaios clínicos, revisões
              sistemáticas e diretrizes clínicas de alta qualidade, reunimos especialistas para uma{" "}
              <strong>Imersão em Prática Baseada em Evidências (PBE)</strong>.
            </p>
            <p>
              O objetivo da imersão é <strong>aproximar a ciência</strong> da <strong>prática</strong>,
              mostrando como transformar artigos científicos em condutas mais seguras, consistentes e
              aplicáveis à realidade do consultório.
            </p>
          </div>
        </div>
      </section>

      {/* PROFESSORES / PROGRAMAÇÃO */}
      <section className="relative overflow-hidden bg-[#0a1420] text-white px-4 sm:px-6 py-20 sm:py-28">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-[420px] w-[720px] rounded-full bg-[#6c63ff]/15 blur-[140px]" />
        </div>
        <div className="relative mx-auto max-w-6xl">
          <div className="text-center">
            <span className="text-xs font-bold uppercase tracking-[0.28em] text-[#3fd5c9]">
              Com quem você vai aprender
            </span>
            <h2 className="mt-4 text-3xl sm:text-5xl font-black tracking-tight">
              Confira o que você vai assistir!
            </h2>
          </div>

          <div className="mt-16 grid gap-12 lg:grid-cols-2">
            <SpeakerCard
              foto={speakersTie.url}
              nome="Tiê Parma e Bruno Saragiotto"
              paragrafos={[
                "Aprenda a fazer perguntas clínicas bem estruturadas e a localizar, de forma eficiente, as melhores evidências disponíveis em bases como a PEDro e a Pubmed.",
                "Um passo essencial para decisões clínicas embasadas na ciência.",
                "Dominar essa habilidade é o primeiro passo para se destacar como um fisioterapeuta atualizado, crítico e confiável.",
              ]}
            />
            <SpeakerCard
              foto={speakersLuciola.url}
              nome="Lucíola Costa e Léo Costa"
              paragrafos={[
                "Descubra como ler artigos científicos de forma crítica e, mais importante, como transformar esse conhecimento em decisões clínicas eficazes.",
                "Você vai aprender a adaptar a evidência ao contexto real do paciente, respeitando suas preferências e características.",
                "Essa é a chave para entregar um atendimento mais resolutivo e conquistar mais confiança e resultado nos seus atendimentos.",
              ]}
            />
          </div>
        </div>
      </section>

      {/* COMO SERÁ A IMERSÃO */}
      <section className="relative bg-[#f4f7fb] text-slate-900 px-4 sm:px-6 py-20 sm:py-28">
        <div className="mx-auto max-w-5xl text-center">
          <span className="text-xs font-bold uppercase tracking-[0.28em] text-[#6c63ff]">
            Sua jornada em Prática Baseada em Evidências
          </span>
          <h2 className="mt-4 text-3xl sm:text-5xl font-black tracking-tight">
            Como funciona a imersão?
          </h2>

          <div className="mt-14 inline-flex flex-col items-start gap-4 rounded-3xl bg-white p-8 sm:p-10 border border-slate-200 shadow-sm text-left">
            <CheckItem>Inscrição 100% gratuita</CheckItem>
            <CheckItem>Aulas gravadas - 100% Online</CheckItem>
            <CheckItem>Certificado de 5h após conclusão das aulas</CheckItem>
            <CheckItem>Acesso ao conteúdo por 12 meses na plataforma</CheckItem>
          </div>

          <div className="mt-14">
            <a
              href="#topo"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#6c63ff] to-[#3fd5c9] text-white font-bold px-9 py-4 shadow-xl shadow-[#6c63ff]/30 hover:shadow-2xl hover:shadow-[#6c63ff]/40 transition"
            >
              Quero me inscrever gratuitamente →
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative bg-[#0a1420] text-white px-4 sm:px-6 py-20 sm:py-28">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <span className="text-xs font-bold uppercase tracking-[0.28em] text-[#3fd5c9]">
              Perguntas Frequentes
            </span>
            <h2 className="mt-4 text-3xl sm:text-5xl font-black tracking-tight">
              Perguntas Frequentes
            </h2>
          </div>
          <div className="mt-12 space-y-3">
            {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

      {/* DOBRA FINAL */}
      <section className="relative bg-[#050b14] text-white px-4 sm:px-6 py-20 sm:py-28 overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[900px] rounded-full bg-[#6c63ff]/15 blur-[160px]" />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[300px] w-[600px] rounded-full bg-[#3fd5c9]/10 blur-[120px]" />
        </div>
        <div className="relative mx-auto max-w-3xl text-center">
          <img loading="lazy" decoding="async"
            src={logoPbe.url}
            alt="Imersão em PBE"
            className="mx-auto h-32 sm:h-44 w-auto drop-shadow-2xl"
          />
          <h2 className="mt-10 text-3xl sm:text-5xl font-black tracking-tight">
            Comece agora. Acesso imediato.
          </h2>
          <p className="mt-5 text-lg sm:text-xl text-white/75 max-w-xl mx-auto">
            Imersão 100% online e gratuita em Prática Baseada em Evidências, com certificado de 5h.
          </p>
          <div className="mt-10">
            <a
              href="#topo"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#6c63ff] to-[#3fd5c9] text-white font-bold px-9 py-4 shadow-xl shadow-[#6c63ff]/30 hover:shadow-2xl hover:shadow-[#6c63ff]/40 transition"
            >
              Quero me inscrever gratuitamente →
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#02061a] text-white/50 text-center text-sm py-6 px-4">
        <p>© {new Date().getFullYear()} Portal Fisio em Ortopedia · Todos os direitos reservados</p>
      </footer>
    </main>
  );
}

function Chip({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 backdrop-blur px-3.5 py-1.5 font-semibold text-white/85">
      {icon}
      {children}
    </span>
  );
}

function SpeakerCard({
  foto,
  nome,
  paragrafos,
}: {
  foto: string;
  nome: string;
  paragrafos: string[];
}) {
  return (
    <article className="rounded-3xl bg-white/[0.03] border border-white/10 overflow-hidden shadow-2xl shadow-black/40">
      <div className="aspect-[4/5] w-full overflow-hidden bg-slate-900">
        <img src={foto} alt={nome} className="h-full w-full object-cover" loading="lazy" />
      </div>
        <div className="p-6 sm:p-8">
        <div className="space-y-3 text-foreground text-center text-base leading-relaxed">
          {paragrafos.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </div>
    </article>
  );
}

function CheckItem({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 text-base sm:text-lg font-bold text-slate-900">
      <span className="flex-shrink-0 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-[#6c63ff] to-[#3fd5c9] text-white w-7 h-7">
        <Check className="w-4 h-4" strokeWidth={3} />
      </span>
      {children}
    </div>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="font-bold text-white">{q}</span>
        <ChevronDown
          className={`w-5 h-5 text-[#3fd5c9] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="px-5 pb-5 text-white/75 leading-relaxed">{a}</div>}
    </div>
  );
}
